import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { ChevronDown, CreditCard, Gift, MinusCircle, Receipt } from 'lucide-react';
import financialApi from 'api/requests/financial';
import Icon from 'components/_UI/Icon';
import Ilustration from 'components/_UI/Ilustration';
import Loader from 'components/_UI/Loader';
import Paginate from 'components/_UI/Paginate';
import Text from 'components/_UI/Text';
import { useEvent } from 'context/events/Event';
import currency from 'helpers/currency';
import { Column } from 'styled/alignment/Column';
import breakpoints from 'theme/breakpoints';
import colors from 'theme/colors';
import fonts from 'theme/fonts';
import EventOrdersFilters from './EventOrdersFilters';
import EventOrdersSummary from './EventOrdersSummary';
import OrderDetailPanel from './OrderDetailPanel';
import { chipOf, CREDITS_BALANCE, CUSTOM_PERIOD, formatDate, formatInt, formatTime, moneyMoved } from './orderTokens';
import { panelAccent } from 'theme/panelAccent';

const DEFAULT_FILTERS = { period: 'all', status: '', payment_method: '', search: '' };

/**
 * `period: 'custom'` só existe para a barra lembrar qual chip está aceso — o
 * backend não conhece esse valor. Enquanto o intervalo não fecha, a lista fica
 * sem recorte de data em vez de mandar um período inválido.
 */
function toApiFilters(filters, eventSlug) {
    const query = { ...filters, event: eventSlug };

    if (query.period === CUSTOM_PERIOD) {
        delete query.period;
    }

    return Object.fromEntries(
        Object.entries(query).filter(([, value]) => value !== undefined && value !== '')
    );
}

/**
 * A forma de pagamento é escolhida no checkout, então carrinho abandonado e
 * reserva expirada chegam sem nenhuma. Eles precisam de ramo próprio: cair no
 * final rotularia como PIX um pedido em que ninguém escolheu pagar assim.
 */
function PaymentLabel({ transaction }) {
    if (!transaction?.payment_method) {
        return (
            <Payment>
                <MinusCircle size={15} color={colors.nightLight} />
                <NotStarted>Não iniciado</NotStarted>
            </Payment>
        );
    }

    if (transaction?.payment_method === 'credit_card') {
        return (
            <Payment>
                <CreditCard size={15} color={colors.night} />
                <span>
                    Cartão
                    {transaction?.installments > 1 ? ` · ${transaction.installments}x` : ''}
                </span>
            </Payment>
        );
    }

    if (transaction?.payment_method === 'free') {
        return (
            <Payment>
                <Gift size={15} color={colors.night} />
                <span>Grátis</span>
            </Payment>
        );
    }

    return (
        <Payment>
            <Column width='15px' height='15px'>
                <Icon name='pix' color={colors.night} />
            </Column>
            <span>{transaction?.payment_method_label || 'PIX'}</span>
        </Payment>
    );
}

/**
 * Aba Pedidos do evento — substitui a antiga aba PDV.
 *
 * A pergunta que ela responde é "o que foi vendido neste evento e quanto disso
 * é meu". Por isso a linha não para no total pago: abrir um pedido revela quem
 * comprou, o que levou e a cadeia inteira do dinheiro, elo por elo.
 *
 * A lista é `/financial/transactions` recortado pelo evento — a mesma fonte da
 * aba Transações do Financeiro, para que o produtor nunca veja dois números
 * diferentes para a mesma venda. Filtro, contagem e exportação são todos
 * resolvidos no backend, então o que a tela diz vale para o evento inteiro e
 * não só para a página carregada.
 */
export default function EventOrders({ data }) {
    const { event } = useEvent();
    const eventSlug = typeof data === 'string' ? data : (data?.slug || event?.slug);

    const [filters, setFilters] = useState(DEFAULT_FILTERS);
    const [transactions, setTransactions] = useState(null);
    const [paginate, setPaginate] = useState(null);
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(true);
    const [summaryLoading, setSummaryLoading] = useState(true);
    const [failed, setFailed] = useState(false);
    const [expandedId, setExpandedId] = useState(null);
    const [exporting, setExporting] = useState(false);
    const [exportError, setExportError] = useState(false);
    /**
     * A busca é servida pelo backend em cima do comprador, e `customer` só
     * chega no payload na versão da API que sabe filtrar por ele. Enquanto
     * produção estiver atrás, o campo não aparece — uma caixa de busca que
     * devolve a lista inteira é pior que busca nenhuma, porque o produtor
     * conclui que o pedido não existe. Mesma regra do `knowsChain` das vendas.
     */
    const [knowsCustomer, setKnowsCustomer] = useState(false);

    const query = useMemo(() => toApiFilters(filters, eventSlug), [filters, eventSlug]);
    const queryKey = JSON.stringify(query);

    /* A faixa de totais é a repartição do evento *por* status — filtrar por um
       status faria o "aguardando pagamento" da faixa sumir junto, e o produtor
       perderia o denominador contra o qual está lendo a lista. Só o recorte de
       período atravessa; status, forma de pagamento e busca ficam na lista. */
    const summaryQuery = useMemo(() => {
        const { status, payment_method, search, ...rest } = query;
        return rest;
    }, [queryKey]);
    const summaryKey = JSON.stringify(summaryQuery);

    /* `period: 'all'` é o padrão, não um recorte: contá-lo como filtro faria o
       estado vazio culpar filtros que o produtor nunca escolheu. */
    const hasNarrowingFilter = Boolean(
        filters.status || filters.payment_method || filters.search || filters.start_date ||
        (filters.period && filters.period !== 'all' && filters.period !== CUSTOM_PERIOD)
    );

    useEffect(() => {
        if (!eventSlug) return;

        setExpandedId(null);
        getTransactions(1);
    }, [queryKey]);

    useEffect(() => {
        if (!eventSlug) return;

        getSummary();
    }, [summaryKey]);

    async function getTransactions(page = 1) {
        setLoading(true);
        setFailed(false);

        try {
            const { data: response } = await financialApi.getOrderTransactions({ ...query, page });
            const rows = response?.data || [];

            setTransactions(rows);
            setPaginate(response?.meta);

            /* Só liga, nunca desliga: uma busca sem resultado devolve zero
               linhas, e reavaliar aqui faria o campo sumir debaixo do cursor. */
            if (rows.some(row => row?.customer !== undefined)) {
                setKnowsCustomer(true);
            }
        } catch (error) {
            /* Sem isso a tela mostrava o estado vazio de "nenhum pedido" quando
               na verdade a requisição caiu — o produtor concluía que não vendeu. */
            setFailed(true);
            setTransactions([]);
        } finally {
            setLoading(false);
        }
    }

    async function getSummary() {
        setSummaryLoading(true);

        try {
            const { data: response } = await financialApi.get(summaryQuery);
            setSummary(response);
        } catch (error) {
            setSummary(null);
        } finally {
            setSummaryLoading(false);
        }
    }

    async function exportTransactions() {
        setExporting(true);
        setExportError(false);

        try {
            const { data: file } = await financialApi.exportOrderTransactions(query);
            const url = window.URL.createObjectURL(file);
            const link = document.createElement('a');

            link.href = url;
            link.setAttribute('download', `pedidos-${eventSlug}-${new Date().toISOString().slice(0, 10)}.csv`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            /* Download é ação explícita: falhar em silêncio deixa o produtor
               clicando de novo sem saber o que aconteceu. */
            setExportError(true);
        } finally {
            setExporting(false);
        }
    }

    /* O estorno muda a linha (status) e a faixa de totais, então os dois são
       recarregados. A linha aberta fecha: ela volta já como estornada. */
    function handleRefunded() {
        setExpandedId(null);
        getTransactions(paginate?.current_page || 1);
        getSummary();
    }

    function toggleRow(id) {
        setExpandedId(current => current === id ? null : id);
    }

    function changePage(page) {
        setExpandedId(null);
        getTransactions(page);
    }

    const isEmpty = !loading && !failed && transactions?.length === 0;

    return (
        <Wrapper>
            <Intro>
                <IntroIcon>
                    <Receipt size={18} color={panelAccent(colors.occasionalPurple)} />
                </IntroIcon>
                <div>
                    <Text type='bold' color={colors.nightDark} paddingBottom={0}>
                        Pedidos do evento
                    </Text>
                    <Text name='small' color={colors.night} paddingBottom={0}>
                        Cada pedido feito neste evento, com o que o comprador pagou, o que a
                        plataforma reteve e o que sobra para você. Abra um pedido para ver quem
                        comprou, o que levou e a conta completa.
                    </Text>
                </div>
            </Intro>

            <EventOrdersSummary data={summary} loading={summaryLoading} />

            {(filters.status || filters.payment_method) && (
                <ScopeNote>
                    Os totais acima continuam mostrando o evento inteiro no período escolhido —
                    é contra eles que a lista filtrada abaixo se lê.
                </ScopeNote>
            )}

            <Panel>
                <EventOrdersFilters
                    filters={filters}
                    onChange={setFilters}
                    onExport={exportTransactions}
                    exporting={exporting}
                    exportError={exportError}
                    canExport={transactions?.length > 0}
                    searchable={knowsCustomer}
                />

                {!loading && !failed && paginate?.total > 0 && (
                    <ResultCount>
                        {formatInt(paginate.total)} {paginate.total === 1 ? 'pedido' : 'pedidos'}
                        {hasNarrowingFilter ? ' com os filtros atuais' : ' neste evento'}
                    </ResultCount>
                )}

                {loading && (
                    <Centered>
                        <Loader />
                    </Centered>
                )}

                {!loading && failed && (
                    <Centered>
                        <Text name='highlight' type='bold' color={colors.nightDark}>
                            Não foi possível carregar os pedidos
                        </Text>
                        <Text name='small' color={colors.night} textAlign='center'>
                            A lista não respondeu agora. Isso não significa que não houve vendas.
                        </Text>
                        <RetryButton type="button" onClick={() => getTransactions(1)}>
                            Tentar de novo
                        </RetryButton>
                    </Centered>
                )}

                {isEmpty && (
                    <Centered>
                        <Column width='auto' height='200px'>
                            <Ilustration name='orders' />
                        </Column>
                        <Text marginTop='16px' name='highlight' type='bold' color={colors.nightDark}>
                            Nenhum pedido encontrado
                        </Text>
                        <Text name='small' color={colors.night} textAlign='center'>
                            {hasNarrowingFilter
                                ? 'Nenhum pedido deste evento bate com os filtros selecionados.'
                                : 'Este evento ainda não recebeu pedidos. Assim que alguém comprar, o pedido aparece aqui.'}
                        </Text>
                    </Centered>
                )}

                {!loading && !failed && transactions?.length > 0 && (
                    <>
                        <DesktopTable>
                            <thead>
                                <tr>
                                    <Th>Pedido</Th>
                                    {knowsCustomer && <Th>Comprador</Th>}
                                    <Th>Pagamento</Th>
                                    <Th align="right">Comprador pagou</Th>
                                    <Th align="right">Taxa retida</Th>
                                    <Th align="right">Seu líquido</Th>
                                    <Th>Status</Th>
                                    <Th aria-label="Detalhes" />
                                </tr>
                            </thead>
                            <tbody>
                                {transactions.map(item => {
                                    const chip = chipOf(item?.status);
                                    const creditsBalance = item?.status === CREDITS_BALANCE;
                                    const moved = moneyMoved(item?.status);
                                    const expanded = expandedId === item?.id;
                                    const panelId = `order-panel-${item?.id}`;

                                    return (
                                        <React.Fragment key={item?.id}>
                                            <Tr $expanded={expanded} onClick={() => toggleRow(item?.id)}>
                                                <Td>
                                                    <OrderId>#{item?.order_id}</OrderId>
                                                    <Secondary>
                                                        {formatDate(item?.created_at)} · {formatTime(item?.created_at)}
                                                    </Secondary>
                                                </Td>
                                                {knowsCustomer && (
                                                    <Td>
                                                        <BuyerName>{item?.customer?.name || '—'}</BuyerName>
                                                        {item?.customer?.email && (
                                                            <BuyerEmail title={item.customer.email}>
                                                                {item.customer.email}
                                                            </BuyerEmail>
                                                        )}
                                                    </Td>
                                                )}
                                                <Td>
                                                    <PaymentLabel transaction={item} />
                                                </Td>
                                                <Td align="right">
                                                    <Money $tone={moved ? 'default' : 'muted'}>
                                                        {currency.format(item?.gross_value)}
                                                    </Money>
                                                </Td>
                                                <Td align="right">
                                                    <Money $tone={moved && Number(item?.tax_value) > 0 ? 'negative' : 'muted'}>
                                                        {Number(item?.tax_value) > 0
                                                            ? `− ${currency.format(item?.tax_value)}`
                                                            : currency.format(0)}
                                                    </Money>
                                                </Td>
                                                <Td align="right">
                                                    <Money $strong $tone={creditsBalance ? 'positive' : 'muted'}>
                                                        {currency.format(item?.net_value)}
                                                    </Money>
                                                </Td>
                                                <Td>
                                                    <StatusBadge $chip={chip}>{chip.label}</StatusBadge>
                                                </Td>
                                                <Td align="right">
                                                    <ToggleButton
                                                        type="button"
                                                        $expanded={expanded}
                                                        aria-expanded={expanded}
                                                        aria-controls={panelId}
                                                        onClick={(e) => { e.stopPropagation(); toggleRow(item?.id); }}
                                                    >
                                                        <ChevronDown size={16} />
                                                        <VisuallyHidden>
                                                            {expanded ? 'Fechar' : 'Abrir'} detalhes do pedido {item?.order_id}
                                                        </VisuallyHidden>
                                                    </ToggleButton>
                                                </Td>
                                            </Tr>

                                            {expanded && (
                                                <ExpandedTr>
                                                    <ExpandedTd colSpan={knowsCustomer ? 8 : 7} id={panelId}>
                                                        <OrderDetailPanel transaction={item} onRefunded={handleRefunded} />
                                                    </ExpandedTd>
                                                </ExpandedTr>
                                            )}
                                        </React.Fragment>
                                    );
                                })}
                            </tbody>
                        </DesktopTable>

                        <MobileCards>
                            {transactions.map(item => {
                                const chip = chipOf(item?.status);
                                const creditsBalance = item?.status === CREDITS_BALANCE;
                                const moved = moneyMoved(item?.status);
                                const expanded = expandedId === item?.id;
                                const panelId = `order-card-${item?.id}`;

                                return (
                                    <MobileCard key={item?.id} $expanded={expanded}>
                                        <MobileHeader
                                            type="button"
                                            aria-expanded={expanded}
                                            aria-controls={panelId}
                                            onClick={() => toggleRow(item?.id)}
                                        >
                                            <MobileHeaderText>
                                                <OrderId>
                                                    {item?.customer?.name || `#${item?.order_id}`}
                                                </OrderId>
                                                <Secondary>
                                                    #{item?.order_id} · {formatDate(item?.created_at)} · {formatTime(item?.created_at)}
                                                </Secondary>
                                            </MobileHeaderText>
                                            <StatusBadge $chip={chip}>{chip.label}</StatusBadge>
                                            <Chevron $expanded={expanded}>
                                                <ChevronDown size={16} />
                                            </Chevron>
                                        </MobileHeader>

                                        <MobileBody>
                                            <MobileField>
                                                <MobileLabel>Pagamento</MobileLabel>
                                                <PaymentLabel transaction={item} />
                                            </MobileField>
                                            <MobileField>
                                                <MobileLabel>{moved ? 'Comprador pagou' : 'Comprador pagaria'}</MobileLabel>
                                                <Money $tone={moved ? 'default' : 'muted'}>
                                                    {currency.format(item?.gross_value)}
                                                </Money>
                                            </MobileField>
                                            <MobileField>
                                                <MobileLabel>Taxa retida</MobileLabel>
                                                <Money $tone={moved && Number(item?.tax_value) > 0 ? 'negative' : 'muted'}>
                                                    {Number(item?.tax_value) > 0
                                                        ? `− ${currency.format(item?.tax_value)}`
                                                        : currency.format(0)}
                                                </Money>
                                            </MobileField>
                                            <MobileField>
                                                <MobileLabel>Seu líquido</MobileLabel>
                                                <Money $strong $tone={creditsBalance ? 'positive' : 'muted'}>
                                                    {currency.format(item?.net_value)}
                                                </Money>
                                            </MobileField>
                                        </MobileBody>

                                        {expanded && (
                                            <MobileExpanded id={panelId}>
                                                <OrderDetailPanel transaction={item} onRefunded={handleRefunded} />
                                            </MobileExpanded>
                                        )}
                                    </MobileCard>
                                );
                            })}
                        </MobileCards>

                        {paginate?.last_page > 1 && (
                            <PaginateWrapper>
                                <Paginate
                                    perPage={paginate?.per_page}
                                    totalItems={paginate?.total}
                                    lastPage={paginate?.last_page}
                                    firstPage={1}
                                    currentPage={paginate?.current_page}
                                    marginTop={'0'}
                                    onPaginate={(number) => changePage(number)}
                                />
                            </PaginateWrapper>
                        )}
                    </>
                )}
            </Panel>
        </Wrapper>
    );
}

/* ── Layout ────────────────────────────────────── */

const Wrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: 14px;
    padding-bottom: 24px;
`;

const Intro = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 12px;
`;

const IntroIcon = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 34px;
    height: 34px;
    border-radius: 9px;
    background: #F3EAFF;
    flex-shrink: 0;
`;

const ScopeNote = styled.p`
    margin: -4px 0 0;
    font-size: ${fonts.sizes.small};
    color: ${colors.night};
    line-height: 1.5;
`;

const Panel = styled.div`
    display: flex;
    flex-direction: column;
    background: ${colors.neutralLight};
    border: 1px solid ${colors.neutralDark};
    border-radius: 14px;
    overflow: hidden;
`;

const ResultCount = styled.p`
    margin: 0;
    padding: 12px 16px 0;
    font-size: ${fonts.sizes.small};
    color: ${colors.night};

    @media screen and (min-width: ${breakpoints.md}) {
        padding: 12px 20px 0;
    }
`;

const Centered = styled(Column)`
    align-items: center;
    justify-content: center;
    gap: 4px;
    padding: 56px 20px;
`;

const RetryButton = styled.button`
    margin-top: 14px;
    padding: 8px 16px;
    border: 1px solid ${colors.neutralDark};
    border-radius: 8px;
    background: ${colors.neutralLight};
    color: ${colors.nightDark};
    font-size: 13px;
    font-family: inherit;
    cursor: pointer;

    &:hover {
        background: ${colors.neutral};
    }

    &:focus-visible {
        outline: 2px solid ${panelAccent(colors.occasionalPurple)};
        outline-offset: 2px;
    }
`;

const PaginateWrapper = styled.div`
    padding: 16px 20px;
    border-top: 1px solid ${colors.neutralDark};
`;

/* ── Tabela (desktop) ──────────────────────────── */

const DesktopTable = styled.table`
    width: 100%;
    border-collapse: collapse;
    display: none;
    margin-top: 12px;

    @media screen and (min-width: ${breakpoints.lg}) {
        display: table;
    }
`;

const Th = styled.th`
    text-align: ${({ align }) => align || 'left'};
    padding: 12px 14px;
    font-size: ${fonts.sizes.small};
    font-weight: 600;
    color: ${colors.night};
    text-transform: uppercase;
    letter-spacing: 0.4px;
    border-bottom: 1px solid ${colors.neutralDark};
    white-space: nowrap;

    &:first-child { padding-left: 20px; }
    &:last-child { padding-right: 20px; width: 44px; }
`;

const Tr = styled.tr`
    cursor: pointer;
    transition: background 0.15s ease;
    border-bottom: 1px solid ${colors.neutralDark};
    background: ${({ $expanded }) => $expanded ? '#FAF6FF' : 'transparent'};

    &:hover {
        background: ${({ $expanded }) => $expanded ? '#FAF6FF' : colors.neutral};
    }
`;

const Td = styled.td`
    padding: 12px 14px;
    vertical-align: middle;
    text-align: ${({ align }) => align || 'left'};

    &:first-child { padding-left: 20px; }
    &:last-child { padding-right: 20px; }
`;

const ExpandedTr = styled.tr`
    border-bottom: 1px solid ${colors.neutralDark};
    background: #FAF6FF;
`;

const ExpandedTd = styled.td`
    padding: 0 20px 18px;
`;

/* ── Conteúdo das células ──────────────────────── */

const OrderId = styled.span`
    display: block;
    font-size: 14px;
    font-weight: 600;
    color: ${colors.nightDark};
`;

const BuyerName = styled.span`
    display: block;
    font-size: 13px;
    color: ${colors.nightDark};
    max-width: 200px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const BuyerEmail = styled.span`
    display: block;
    font-size: ${fonts.sizes.small};
    color: ${colors.night};
    margin-top: 2px;
    max-width: 200px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const Secondary = styled.span`
    display: block;
    font-size: ${fonts.sizes.small};
    color: ${colors.night};
    margin-top: 2px;
`;

const Payment = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    color: ${colors.nightDark};
    white-space: nowrap;
`;

/* Apagado como os valores da mesma linha: não houve pagamento para relatar. */
const NotStarted = styled.span`
    color: ${colors.nightLight};
`;

const Money = styled.span`
    font-size: ${({ $strong }) => $strong ? '14px' : '13px'};
    font-weight: ${({ $strong }) => $strong ? '600' : '400'};
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
    color: ${({ $tone }) => {
        if ($tone === 'negative') return '#B3261E';
        if ($tone === 'positive') return '#157F3C';
        if ($tone === 'muted') return colors.nightLight;
        return colors.nightDark;
    }};
`;

const StatusBadge = styled.span`
    display: inline-flex;
    align-items: center;
    padding: 3px 11px;
    border-radius: 50px;
    font-size: ${fonts.sizes.small};
    font-weight: 600;
    white-space: nowrap;
    background: ${({ $chip }) => $chip.bg};
    color: ${({ $chip }) => $chip.text};
    border: 1px solid ${({ $chip }) => $chip.border};
`;

const ToggleButton = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    border: none;
    border-radius: 8px;
    background: transparent;
    color: ${colors.night};
    cursor: pointer;
    transition: transform 0.2s ease, background 0.15s ease;
    transform: rotate(${({ $expanded }) => $expanded ? '180deg' : '0deg'});

    &:hover {
        background: ${colors.neutralDark};
    }

    &:focus-visible {
        outline: 2px solid ${panelAccent(colors.occasionalPurple)};
        outline-offset: 2px;
    }
`;

const VisuallyHidden = styled.span`
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0 0 0 0);
    white-space: nowrap;
    border: 0;
`;

/* ── Cards (mobile) ────────────────────────────── */

const MobileCards = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 12px 16px 4px;

    @media screen and (min-width: ${breakpoints.lg}) {
        display: none;
    }
`;

const MobileCard = styled.div`
    border: 1px solid ${({ $expanded }) => $expanded ? '#DFC8F8' : colors.neutralDark};
    border-radius: 10px;
    overflow: hidden;
    background: ${({ $expanded }) => $expanded ? '#FAF6FF' : colors.neutralLight};
    transition: all 0.15s ease;
`;

const MobileHeader = styled.button`
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
    padding: 12px 14px;
    border: none;
    border-bottom: 1px solid ${colors.neutralDark};
    background: transparent;
    font-family: inherit;
    text-align: left;
    cursor: pointer;

    &:focus-visible {
        outline: 2px solid ${panelAccent(colors.occasionalPurple)};
        outline-offset: -2px;
    }
`;

const MobileHeaderText = styled.span`
    flex: 1;
    min-width: 0;
`;

const Chevron = styled.span`
    display: inline-flex;
    color: ${colors.night};
    transition: transform 0.2s ease;
    transform: rotate(${({ $expanded }) => $expanded ? '180deg' : '0deg'});
`;

const MobileBody = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 12px 14px;
`;

const MobileField = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
`;

const MobileLabel = styled.span`
    font-size: ${fonts.sizes.small};
    font-weight: 600;
    color: ${colors.night};
`;

const MobileExpanded = styled.div`
    padding: 0 14px 14px;
`;
